import { Skeleton } from '@/components/ui/skeleton'
import { getMyRecentWorkdays } from '@/modules/Dashboard/services'
import { MyRecentWorkday } from '@/modules/Dashboard/types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/vi'
import isoWeek from 'dayjs/plugin/isoWeek'
import {
    Clock,
    CalendarDays,
    Coffee,
    MapPin,
    ChevronLeft,
    ChevronRight,
    HelpCircle,
    ExternalLink,
} from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'

dayjs.extend(isoWeek)

// Vietnamese day abbreviations
const VI_DAY_ABBR = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
const VI_DAY_NAMES = [
    'Chủ nhật',
    'Thứ 2',
    'Thứ 3',
    'Thứ 4',
    'Thứ 5',
    'Thứ 6',
    'Thứ 7',
]

// Status types for styling
type WorkdayStatus =
    | 'normal'
    | 'late'
    | 'leave'
    | 'remote'
    | 'holiday'
    | 'business'
    | 'no_data'
    | 'future'

const MyWorkdaysSkeleton = () => (
    <div className="space-y-1">
        {[0, 1, 2, 3, 4].map((i) => (
            <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg border border-gray-100"
            >
                <div className="flex flex-col items-center w-10">
                    <Skeleton className="h-3 w-6 mb-1" />
                    <Skeleton className="h-7 w-7" />
                </div>
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-3 w-32" />
                </div>
            </div>
        ))}
    </div>
)

// Check if late based on work_day_option
const isLateCheckIn = (workday: MyRecentWorkday): boolean => {
    if (!workday.work_day_in) return false
    const [hours, minutes] = workday.work_day_in.split(':').map(Number)
    const checkInMinutes = hours * 60 + minutes
    const isMorningShift =
        workday.work_day_option === 0 ||
        workday.work_day_option === 2 ||
        workday.work_day_option === null
    const lateThreshold = isMorningShift ? 8 * 60 + 30 : 13 * 60
    return checkInMinutes > lateThreshold
}

// Get shift label
const getShiftLabel = (
    workday: MyRecentWorkday,
    t: (key: string) => string
): string | null => {
    if (workday.day_off?.day_off_item_option) {
        if (workday.day_off.day_off_item_option === 'morning')
            return t('my_workdays.shift.morning')
        if (workday.day_off.day_off_item_option === 'afternoon')
            return t('my_workdays.shift.afternoon')
        if (workday.day_off.day_off_item_option === 'allDay')
            return t('my_workdays.shift.all_day')
    }
    if (workday.business?.business_option) {
        if (workday.business.business_option === '0')
            return t('my_workdays.shift.morning')
        if (workday.business.business_option === '1')
            return t('my_workdays.shift.afternoon')
        if (workday.business.business_option === '2')
            return t('my_workdays.shift.all_day')
    }
    return null
}

// Determine workday status
const getWorkdayStatus = (
    workday: MyRecentWorkday | null,
    isFuture: boolean
): WorkdayStatus => {
    if (isFuture) return 'future'
    if (!workday) return 'no_data'

    const type = workday.work_day_type.toLowerCase()
    const hasDayoff = !!workday.day_off
    const hasBusiness = !!workday.business

    if (hasBusiness || type.includes('công tác')) return 'business'
    if (type.includes('nghỉ lễ') || type.includes('tết')) return 'holiday'
    if (type.includes('remote')) return 'remote'
    if (type.includes('nghỉ phép') || type.includes('không lương') || hasDayoff)
        return 'leave'
    if (workday.work_day_in && isLateCheckIn(workday)) return 'late'
    if (workday.work_day_in) return 'normal'

    return 'no_data'
}

// Get icon for status
const getStatusIcon = (status: WorkdayStatus) => {
    switch (status) {
        case 'normal':
        case 'late':
            return Clock
        case 'leave':
        case 'holiday':
            return Coffee
        case 'remote':
            return MapPin
        case 'business':
            return MapPin
        default:
            return HelpCircle
    }
}

// Get icon background color
const getIconBgColor = (status: WorkdayStatus): string => {
    switch (status) {
        case 'normal':
            return 'bg-green-100 text-green-600'
        case 'late':
            return 'bg-green-100 text-green-600'
        case 'leave':
            return 'bg-orange-100 text-orange-500'
        case 'holiday':
            return 'bg-orange-100 text-orange-500'
        case 'remote':
            return 'bg-blue-100 text-blue-600'
        case 'business':
            return 'bg-blue-100 text-blue-600'
        case 'no_data':
        case 'future':
        default:
            return 'bg-gray-100 text-gray-400'
    }
}

// Get status display text
const getStatusText = (
    workday: MyRecentWorkday | null,
    status: WorkdayStatus,
    t: (key: string) => string
): string => {
    if (status === 'future') return t('my_workdays.upcoming')
    if (status === 'no_data') return t('my_workdays.no_record')

    if (!workday) return ''

    const shiftLabel = getShiftLabel(workday, t)

    // For leave/dayoff
    if (status === 'leave' || status === 'holiday') {
        const baseText = workday.work_day_type
        return shiftLabel ? `${baseText} (${shiftLabel})` : baseText
    }

    // For business trip
    if (status === 'business') {
        return shiftLabel
            ? `${t('my_workdays.business')} (${shiftLabel})`
            : t('my_workdays.business')
    }

    // For remote
    if (status === 'remote') {
        return t('my_workdays.remote_work')
    }

    // For normal/late - show check-in/out times
    if (workday.work_day_in && workday.work_day_out) {
        const hours = workday.work_day_time?.toFixed(1) || '0'
        return `Vào: ${workday.work_day_in}  •  Ra: ${workday.work_day_out}  •  ${hours}h`
    }

    if (workday.work_day_in) {
        return `Vào: ${workday.work_day_in}`
    }

    return workday.work_day_type
}

interface DaySlotProps {
    date: Dayjs
    workday: MyRecentWorkday | null
    isFuture: boolean
}

const DaySlot: React.FC<DaySlotProps> = ({ date, workday, isFuture }) => {
    const { t } = useTranslation('dashboard')
    const isToday = date.isSame(dayjs(), 'day')
    const dayOfWeek = date.day() // 0 = Sunday, 1 = Monday, etc.

    const status = getWorkdayStatus(workday, isFuture)
    const Icon = getStatusIcon(status)
    const iconBgColor = getIconBgColor(status)
    const statusText = getStatusText(workday, status, t)
    const isLate = status === 'late' || (workday && isLateCheckIn(workday))

    return (
        <div
            className={`
                flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 transition-all
                ${isToday ? 'bg-blue-50/70 border-l-4 border-l-primary' : 'bg-white'}
                ${isFuture ? 'opacity-50' : ''}
            `}
        >
            {/* Date Column */}
            <div className="flex flex-col items-center justify-center w-9 shrink-0">
                <span className="text-[10px] text-gray-400 font-medium">
                    {VI_DAY_ABBR[dayOfWeek]}
                </span>
                <span
                    className={`text-xl font-bold leading-tight ${isToday ? 'text-primary' : 'text-gray-800'}`}
                >
                    {date.format('DD')}
                </span>
            </div>

            {/* Icon */}
            <div
                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${iconBgColor}`}
            >
                <Icon className="w-4 h-4" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[13px] font-semibold text-primary">
                        {VI_DAY_NAMES[dayOfWeek]}
                    </span>
                    {isToday && (
                        <span className="text-[9px] font-medium bg-primary text-white px-2 py-0.5 rounded">
                            {t('my_workdays.today')}
                        </span>
                    )}
                </div>
                <span
                    className={`text-[12px] block ${status === 'no_data' || status === 'future' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                    {statusText}
                </span>
            </div>

            {/* Status Badge */}
            {isLate && !isFuture && (
                <span className="text-[11px] font-medium bg-red-50 text-red-500 px-3 py-1 rounded-full shrink-0">
                    {t('my_workdays.late')}
                </span>
            )}
        </div>
    )
}

const MyWorkdays: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const today = dayjs()
    const startOfYear = today.startOf('year').startOf('isoWeek')

    // Week state - default to current week
    const [weekStart, setWeekStart] = useState<Dayjs>(() => {
        return today.startOf('isoWeek')
    })

    // Calculate week range for API query (Mon-Sat)
    const dateRange = useMemo(() => {
        const weekEnd = weekStart.add(5, 'day')
        return {
            gte: weekStart.format('YYYY-MM-DD'),
            lte: weekEnd.format('YYYY-MM-DD'),
        }
    }, [weekStart])

    // Check if prev week is allowed (min: start of year)
    const isPrevWeekDisabled = useMemo(() => {
        return weekStart.subtract(1, 'week').isBefore(startOfYear, 'week')
    }, [weekStart, startOfYear])

    // Check if next week is allowed (max: current week)
    const isNextWeekDisabled = useMemo(() => {
        return weekStart.isSame(today, 'isoWeek')
    }, [weekStart, today])

    const handlePrevWeek = () => {
        if (isPrevWeekDisabled) return
        setWeekStart(weekStart.subtract(1, 'week'))
    }

    const handleNextWeek = () => {
        if (isNextWeekDisabled) return
        setWeekStart(weekStart.add(1, 'week'))
    }

    // Format week label
    const weekLabel = useMemo(() => {
        const weekEnd = weekStart.add(4, 'day')
        return `${weekStart.format('DD/MM')} - ${weekEnd.format('DD/MM')}`
    }, [weekStart])

    const isCurrentWeek = weekStart.isSame(today, 'isoWeek')

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MY_RECENT_WORKDAYS,
                dateRange.gte,
                dateRange.lte,
            ],
            queryFn: () => getMyRecentWorkdays(dateRange),
            staleTime: 5 * 60 * 1000,
        },
        {
            loadingComponent: <MyWorkdaysSkeleton />,
            errorDisplayMode: 'page',
        }
    )

    const stateComponent = renderState()

    // Generate day slots (Mon-Sat)
    const daySlots = useMemo(() => {
        const workdays = data || []
        const workdayMap = new Map<string, MyRecentWorkday>()

        workdays.forEach((w: MyRecentWorkday) => {
            const dateKey = dayjs(w.work_day_date).format('YYYY-MM-DD')
            workdayMap.set(dateKey, w)
        })

        // Generate 6 days (Mon-Sat)
        const slots: {
            date: Dayjs
            workday: MyRecentWorkday | null
        }[] = []

        for (let i = 0; i < 6; i++) {
            const date = weekStart.add(i, 'day')
            const dateKey = date.format('YYYY-MM-DD')
            const workday = workdayMap.get(dateKey) || null
            slots.push({ date, workday })
        }

        return slots
    }, [data, weekStart])

    const totalDays = useMemo(() => {
        return daySlots.reduce(
            (sum, slot) => sum + (slot.workday?.work_n_day || 0),
            0
        )
    }, [daySlots])

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-gray-900 text-[13px]">
                        {t('my_workdays.title')}
                    </span>
                </div>
                <Button
                    variant="text"
                    size="small"
                    className="p-0 h-auto text-[11px] text-primary hover:text-primary/80"
                    onClick={() =>
                        navigate(
                            generatePathDatacentral(paths.workdayManage, {})
                        )
                    }
                >
                    {t('my_workdays.view_detail')}
                    <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
            </div>

            {/* Week Selector */}
            <div className="flex items-center justify-between gap-1.5 mb-2 bg-gray-50 rounded-md px-1.5 py-1">
                <button
                    onClick={handlePrevWeek}
                    disabled={isPrevWeekDisabled}
                    className={`p-1 rounded-full transition-colors ${
                        isPrevWeekDisabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-semibold text-gray-700">
                        {weekLabel}
                    </span>
                    {isCurrentWeek && (
                        <span className="text-[9px] font-medium bg-primary text-white px-1.5 py-0.5 rounded">
                            {t('my_workdays.this_week')}
                        </span>
                    )}
                    <span className="text-[11px] text-gray-500">
                        ({totalDays} {t('my_workdays.total_days')})
                    </span>
                </div>

                <button
                    onClick={handleNextWeek}
                    disabled={isNextWeekDisabled}
                    className={`p-1 rounded-full transition-colors ${
                        isNextWeekDisabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            {/* Workday List */}
            <div className="flex-1 overflow-y-auto rounded-xl border border-gray-100 bg-white overflow-hidden">
                {stateComponent
                    ? stateComponent
                    : daySlots.map((slot) => (
                          <DaySlot
                              key={slot.date.format('YYYY-MM-DD')}
                              date={slot.date}
                              workday={slot.workday}
                              isFuture={slot.date.isAfter(today, 'day')}
                          />
                      ))}
            </div>
        </div>
    )
}

export default MyWorkdays
